import { DataProviderProxy } from 'ra-core'
import { parseSettingsFromJSON } from 'resources/systemTemplates/utils'
import { osFetch } from 'util/fetch'

export function transformTemplateForAutoDesign(template: any): any {
  if (!template) return template

  const parsedSettings = parseSettingsFromJSON(template.settings)
  const parsedMountingConfig = parseSettingsFromJSON(template.mounting_config)
  // Parse battery_design_assistant and inverter_design_assistant if they're JSON strings at top level
  const parsedBatteryDesignAssistant =
    typeof template.battery_design_assistant === 'string'
      ? parseSettingsFromJSON(template.battery_design_assistant)
      : template.battery_design_assistant
  const parsedInverterDesignAssistant =
    typeof template.inverter_design_assistant === 'string'
      ? parseSettingsFromJSON(template.inverter_design_assistant)
      : template.inverter_design_assistant

  // Extract fields from settings to top level (prefer top-level if already present)
  const transformed = {
    ...template,
    components_and_quantities: template.components_and_quantities || parsedSettings?.components_and_quantities || [],
    constraints: template.constraints || parsedSettings?.constraints || [],
    design_type: template.design_type || parsedSettings?.design_type || null,
    azimuth_from_to: template.azimuth_from_to || parsedSettings?.azimuth_from_to || null,
    orientation_options: template.orientation_options || parsedSettings?.orientation_options || null,
    battery_design_assistant: parsedBatteryDesignAssistant || parsedSettings?.battery_design_assistant || null,
    inverter_design_assistant: parsedInverterDesignAssistant || parsedSettings?.inverter_design_assistant || null,
    mounting_config: parsedMountingConfig, // Parse mounting_config if it's a JSON string
    settings: parsedSettings, // Keep settings as parsed object
  }

  // Normalize the template to match what the auto-design API expects
  return normalizeTemplateForAutoDesign(transformed)
}

function normalizeTemplateForAutoDesign(template: any): any {
  if (!template) return template

  // Extract ID from URL if needed (some templates come with url instead of id)
  let id = template.id
  if (!id && template.url) {
    const match = template.url.match(/system_templates\/(\d+)/)
    if (match) {
      id = parseInt(match[1], 10)
    }
  }

  // Validate that we have an ID
  if (!id) {
    console.warn('[normalizeTemplateForAutoDesign] Template missing ID:', template)
    throw new Error('Template must have an id or url field')
  }

  // Normalize components_and_quantities
  const normalizedComponents = (template.components_and_quantities || []).map((comp: any) => {
    const normalized: any = {
      code: comp.code,
      quantity: comp.quantity,
    }
    // Include id if available, but remove component_type
    if (comp.id !== undefined) {
      normalized.id = comp.id
    }
    return normalized
  })

  // Normalize constraints
  const normalizedConstraints = (template.constraints || []).map((constraint: any) => ({
    type: constraint.type,
    min: constraint.min,
    max: constraint.max,
    ...(constraint.value !== undefined && { value: constraint.value }),
  }))

  // Normalize azimuth_from_to: convert null to empty array
  const normalizedAzimuth = template.azimuth_from_to === null ? [] : template.azimuth_from_to || []

  // Helper function to extract ID from URL or return the ID if it's already a number
  const extractIdFromUrlOrId = (value: any, idField?: any): number | null => {
    if (value === null || value === undefined) {
      // Try the _id field if available
      if (idField !== undefined && idField !== null) {
        return typeof idField === 'number' ? idField : null
      }
      return null
    }
    if (typeof value === 'number') {
      return value
    }
    if (typeof value === 'string') {
      // Check if it's a URL (contains '/')
      if (value.includes('/')) {
        // Extract ID from URL like "http://localhost:30000/api/orgs/1035/costings/428/"
        const match = value.match(/\/(\d+)\/?$/)
        if (match) {
          return parseInt(match[1], 10)
        }
      } else {
        // It's a string that might be a number
        const parsed = parseInt(value, 10)
        if (!isNaN(parsed)) {
          return parsed
        }
      }
    }
    // If value is an object with an id property
    if (typeof value === 'object' && value !== null && 'id' in value) {
      return typeof value.id === 'number' ? value.id : null
    }
    return null
  }

  // Extract IDs from hyperlinked fields or use _id convenience fields
  const pricingSchemeId = extractIdFromUrlOrId(template.pricing_scheme, template.pricing_scheme_id)
  const costingOverrideId = extractIdFromUrlOrId(template.costing_override, template.costing_override_id)
  const commissionOverrideId = extractIdFromUrlOrId(template.commission_override, template.commission_override_id)

  // Helper to extract IDs from arrays of URLs or IDs
  const extractIdsFromArray = (value: any): number[] | null => {
    if (!value || !Array.isArray(value)) {
      return null
    }
    const ids = value.map((item) => extractIdFromUrlOrId(item)).filter((id): id is number => id !== null)
    return ids.length > 0 ? ids : null
  }

  const paymentOptionsOverrideIds = extractIdsFromArray(template.payment_options_override)
  const incentivesOverrideIds = extractIdsFromArray(template.incentives_override)
  const addersOverrideIds = extractIdsFromArray(template.adders_override)

  // Build normalized template with only the fields the API expects
  // Include all override fields explicitly (even if null) to match the expected format
  const normalized: any = {
    id: id,
    name: template.name,
    description: template.description || null,
    design_type: template.design_type || null,
    components_and_quantities: normalizedComponents,
    constraints: normalizedConstraints,
    azimuth_from_to: normalizedAzimuth,
    orientation_options: template.orientation_options || null,
    battery_design_assistant: template.battery_design_assistant || null,
    inverter_design_assistant: template.inverter_design_assistant || null,
    mounting_config: template.mounting_config || null,
    system_templates_auto: template.system_templates_auto || false,
    pricing_scheme_id: pricingSchemeId,
    commission_override_id: commissionOverrideId,
    payment_options_override: paymentOptionsOverrideIds,
    incentives_override: incentivesOverrideIds,
    adders_override: addersOverrideIds,
    costing_override: costingOverrideId,
    battery_control_scheme_override:
      template.battery_control_scheme_override !== undefined ? template.battery_control_scheme_override : null,
  }

  return normalized
}

interface ResolveSystemTemplatesArgs {
  template_name?: string
  template_id?: number
  system_templates_json?: any[]
}

export async function resolveSystemTemplatesWithDataProvider(
  args: ResolveSystemTemplatesArgs,
  orgId: number | undefined,
  dataProvider: DataProviderProxy
): Promise<any[]> {
  if (!orgId) {
    throw new Error('Organization ID is required to run auto design')
  }

  // Prefer system_templates_json - the chatbot should use search_records to find templates
  // Templates from search_records have settings as JSON string, so we fetch full templates and transform them
  if (args.system_templates_json && Array.isArray(args.system_templates_json)) {
    // Always fetch full templates (they may have settings as JSON string) and transform to proper format
    const fullTemplates = await Promise.all(
      args.system_templates_json.map(async (template: any) => {
        if (template.id) {
          const { data: fullTemplate } = await dataProvider.getOne('system_templates', { id: template.id })
          return transformTemplateForAutoDesign(fullTemplate)
        }
        return transformTemplateForAutoDesign(template)
      })
    )
    return fullTemplates
  } else if (args.template_id) {
    // Fallback: Fetch template by ID if only ID is provided
    const { data: template } = await dataProvider.getOne('system_templates', { id: args.template_id })
    return [transformTemplateForAutoDesign(template)]
  } else if (args.template_name) {
    const searchQuery = args.template_name.trim()
    const { data: templates } = await dataProvider.getList('system_templates', {
      filter: { q: searchQuery },
      pagination: { page: 1, perPage: 20 }, // Get more results to find the best match
      sort: { field: 'updated_at', order: 'DESC' },
    })

    if (templates && templates.length > 0) {
      // Find the best match - prefer exact matches, then contains matches
      const lowerSearchQuery = searchQuery.toLowerCase()
      let bestMatch = templates.find((t: any) => t.name && t.name.toLowerCase() === lowerSearchQuery)

      if (!bestMatch) {
        // Try to find a match that contains the full search query
        bestMatch = templates.find((t: any) => t.name && t.name.toLowerCase().includes(lowerSearchQuery))
      }

      if (!bestMatch) {
        // Use the first result (most recently updated, which is what search_records would return)
        bestMatch = templates[0]
      }

      const { data: fullTemplate } = await dataProvider.getOne('system_templates', { id: bestMatch.id })
      return [transformTemplateForAutoDesign(fullTemplate)]
    } else {
      throw new Error(`Template "${args.template_name}" not found`)
    }
  } else {
    throw new Error('Either template_name, template_id, or system_templates_json must be provided')
  }
}

export async function resolveSystemTemplatesWithRestAPI(
  args: ResolveSystemTemplatesArgs,
  orgId: number | undefined
): Promise<any[]> {
  if (!orgId) {
    throw new Error('Organization ID is required to run auto design')
  }

  const orgIdStr = orgId.toString()

  // Prefer system_templates_json - the chatbot should use search_records to find templates
  // and pass the full template data here. Fallbacks are provided for convenience.
  if (args.system_templates_json && Array.isArray(args.system_templates_json)) {
    // Transform templates (they may have settings as JSON string)
    return args.system_templates_json.map(transformTemplateForAutoDesign)
  } else if (args.template_id) {
    // Fallback: Fetch template by ID using REST API and transform it
    const response = await osFetch(`${window.API_ROOT}/api/orgs/${orgIdStr}/system_templates/${args.template_id}/`, {
      headers: window.Utils?.tokenAuthHeaders({ 'Content-Type': 'application/json' }) || {},
    })
    if (!response.ok) {
      throw new Error(`Failed to fetch template: ${response.statusText}`)
    }
    const template = await response.json()
    return [transformTemplateForAutoDesign(template)]
  } else if (args.template_name) {
    const response = await osFetch(
      `${window.API_ROOT}/api/orgs/${orgIdStr}/system_templates/?search=${encodeURIComponent(
        args.template_name
      )}&limit=1`,
      {
        headers: window.Utils?.tokenAuthHeaders({ 'Content-Type': 'application/json' }) || {},
      }
    )
    if (!response.ok) {
      throw new Error(`Failed to search templates: ${response.statusText}`)
    }
    const data = await response.json()
    if (data.results && data.results.length > 0) {
      // Fetch full template data (list endpoint may not include all fields)
      const fullTemplateResponse = await osFetch(
        `${window.API_ROOT}/api/orgs/${orgIdStr}/system_templates/${data.results[0].id}/`,
        {
          headers: window.Utils?.tokenAuthHeaders({ 'Content-Type': 'application/json' }) || {},
        }
      )
      if (fullTemplateResponse.ok) {
        const fullTemplate = await fullTemplateResponse.json()
        return [transformTemplateForAutoDesign(fullTemplate)]
      } else {
        return [transformTemplateForAutoDesign(data.results[0])]
      }
    } else {
      throw new Error(`Template "${args.template_name}" not found`)
    }
  } else {
    throw new Error('Either template_name, template_id, or system_templates_json must be provided')
  }
}
